/* Voice notes. The analysis-critical payload — the client said so directly
 * (the sacred trio: photo, GPS, spoken note). This path gets paranoia.
 *
 * Two things bite on iOS and are handled here rather than fought:
 *   · mime support varies. Ask isTypeSupported, fall back to audio/mp4, and if
 *     nothing answers, let the browser pick and record whatever it gives us.
 *   · the screen dims mid-sentence and the tab can be suspended. A wake lock is
 *     held for the duration of a recording, best-effort.
 */

const CANDIDATES = [
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

export function pickMime(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const m of CANDIDATES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return ''; // let the browser choose; we store whatever it produces
}

export const extensionFor = (mime: string): string => {
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  return 'webm';
};

export interface Recording {
  blob: Blob;
  mime: string;
  duration: number;
}

type LevelFn = (level: number) => void;

export class VoiceRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private wakeLock: WakeLockSentinel | null = null;
  private audioCtx: AudioContext | null = null;
  private raf = 0;

  /** Live input level 0–1, for the waveform. */
  onLevel: LevelFn | null = null;

  get recording(): boolean {
    return this.recorder?.state === 'recording';
  }

  get elapsed(): number {
    return this.startedAt ? (Date.now() - this.startedAt) / 1000 : 0;
  }

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false, // it's a rock, not a conference call
        noiseSuppression: false, // wind is context; don't let the browser guess
        autoGainControl: true,
      },
    });

    const mime = pickMime();
    this.recorder = new MediaRecorder(this.stream, mime ? { mimeType: mime } : undefined);
    this.chunks = [];
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    // Timeslice so a crash mid-note still leaves usable chunks behind.
    this.recorder.start(1000);
    this.startedAt = Date.now();

    this.meter();
    void this.lock();
  }

  async stop(): Promise<Recording | null> {
    const rec = this.recorder;
    if (!rec || rec.state === 'inactive') return null;

    const duration = this.elapsed;
    const blob = await new Promise<Blob>((resolve) => {
      rec.onstop = () =>
        resolve(new Blob(this.chunks, { type: rec.mimeType || 'audio/mp4' }));
      rec.stop();
    });

    this.teardown();
    return { blob, mime: rec.mimeType || 'audio/mp4', duration };
  }

  cancel(): void {
    try {
      this.recorder?.stop();
    } catch {
      /* already stopped */
    }
    this.teardown();
  }

  private teardown(): void {
    cancelAnimationFrame(this.raf);
    this.stream?.getTracks().forEach((t) => t.stop());
    void this.audioCtx?.close().catch(() => {});
    void this.wakeLock?.release().catch(() => {});
    this.stream = null;
    this.recorder = null;
    this.audioCtx = null;
    this.wakeLock = null;
    this.startedAt = 0;
  }

  private async lock(): Promise<void> {
    try {
      this.wakeLock = (await navigator.wakeLock?.request('screen')) ?? null;
    } catch {
      /* denied or unsupported — recording continues regardless */
    }
  }

  private meter(): void {
    if (!this.stream || !this.onLevel) return;
    try {
      const Ctx = window.AudioContext ?? (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx = new Ctx();
      const source = this.audioCtx.createMediaStreamSource(this.stream);
      const analyser = this.audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let peak = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = Math.abs(buf[i] - 128) / 128;
          if (v > peak) peak = v;
        }
        this.onLevel?.(peak);
        this.raf = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      /* no meter — the elapsed counter still tells you it's running */
    }
  }
}

export { formatDuration } from '@kilroy/shared';
